import { useEffect, useState } from "react";
import { WarehouseDataType } from "../../interfaces/admin.interface";
import { Helmet } from "react-helmet";
import { message, Modal, Popconfirm, Table, TableProps } from "antd";
import * as Yup from "yup";
import { useFormik } from "formik";
import ErrorMessage from "../../components/shared/ErrorMessage/ErrorMessage";
import MainButton from "../../components/shared/MainButton/MainButton";
import Spinner from "../../components/shared/Spinner/Spinner";
import { FaPlus } from "react-icons/fa";
import { WarehouseService } from "../../services/warehouse.service";

const Warehouse = () => {
  const [warehouse, setWarehouse] = useState<WarehouseDataType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingRecord, setEditingRecord] = useState<WarehouseDataType | null>(
    null
  );

  useEffect(() => {
    setLoading(true);
    WarehouseService.getAllwarehouse()
      .then((response) => {
        if (Array.isArray(response?.data?.data)) {
          const dataWithKeys = response?.data?.data?.map(
            (item: WarehouseDataType, index: number) => ({
              ...item,
              key: index + 1,
            })
          );
          setWarehouse(dataWithKeys);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);
  const handleAfterSubmit = async () => {
    setLoading(true);
    setIsModalVisible(false);
    resetForm();
    const updatedData = await WarehouseService.getAllwarehouse();
    setWarehouse(
      updatedData?.data?.data.map((item: WarehouseDataType, index: number) => ({
        ...item,
        key: index + 1,
      }))
    );
  };
  const {
    handleSubmit,
    values,
    resetForm,
    setFieldValue,
    touched,
    errors,
    handleChange,
  } = useFormik({
    initialValues: {
      warehouse_name: "",
      warehouse_code: "",
    },
    validationSchema: Yup.object({
      warehouse_name: Yup.string().required("Anbarın adı tələb olunur"),
      warehouse_code: Yup.string().required("Anbarın kodu tələb olunur"),
    }),
    onSubmit: async (values) => {
      if (isEditing && editingRecord) {
        const isEqual =
          editingRecord.warehouse_code === values.warehouse_code &&
          editingRecord.warehouse_name === values.warehouse_name;
        if (isEqual) {
          message.info("Dəyişiklik edilmədi.");
        } else {
          const response = await WarehouseService.updateWarehouse({
            ...values,
            id: editingRecord.id,
          });
          if (response.status === 200) {
            message.success("Anbar uğurla yeniləndi!");
            await handleAfterSubmit();
          } else {
            message.error(
              response.errorMessage || "Anbarı ​​yeniləmək alınmadı."
            );
          }
        }
      } else {
        const response = await WarehouseService.addWarehouse(values);
        if (response.status === 200) {
          message.success("Anbar uğurla əlavə edildi!");
          await handleAfterSubmit();
        } else {
          message.error(response.errorMessage || "Anbar əlavə etmək alınmadı.");
        }
      }
      setLoading(false);
    },
  });
  const handleDeleteClick = async (record: WarehouseDataType) => {
    try {
      await WarehouseService.deleteWarehouse(record.id.toString());
      setWarehouse((prevData) =>
        prevData.filter((item) => item.id !== record.id)
      );
      message.success("Anbar uğurla silindi!");
    } catch (error) {
      message.error("Anbarı ​​silmək alınmadı.");
    }
  };

  const columns: TableProps<WarehouseDataType>["columns"] = [
    {
      title: "№",
      dataIndex: "key",
      sorter: (a, b) => a.key - b.key,
    },
    {
      title: "Anbarın adı",
      dataIndex: "warehouse_name",
      sorter: (a, b) => a.warehouse_name.localeCompare(b.warehouse_name),
    },
    {
      title: "Anbarın kodu",
      dataIndex: "warehouse_code",
    },
    {
      title: (
        <div className="settings">
          <i className="fa-solid fa-gear"></i>
        </div>
      ),
      key: "action",
      render: (_, record) => (
        <div className="settings_icons">
          <div className="edit" onClick={() => handleEditClick(record)}>
            <i className="fa-solid fa-pen-to-square"></i>
          </div>
          <Popconfirm
            title="Bu anbarı siləcəyinizə əminsiniz?"
            onConfirm={() => handleDeleteClick(record)}
            onCancel={() => message.info(" Silmə əməliyyatı ləğv edildi.")}
            okText="Bəli"
            cancelText="Xeyr"
            placement="topRight"
          >
            <div className="delete">
              <i className="fa-solid fa-trash"></i>
            </div>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const handleAddClick = () => {
    setIsEditing(false);
    setEditingRecord(null);
    setIsModalVisible(true);
    resetForm();
  };
  const handleEditClick = (record: WarehouseDataType) => {
    setIsEditing(true);
    setEditingRecord(record);
    setIsModalVisible(true);
    setFieldValue("warehouse_name", record.warehouse_name);
    setFieldValue("warehouse_code", record.warehouse_code);
  };

  return (
    <div id="pages">
      <Helmet>
        <title>1412 | Anbarlar</title>
      </Helmet>
      {loading ? (
        <Spinner />
      ) : (
        <div>
          <div className="page_head">
            <h3 className="page_title">Anbarlar</h3>
          </div>
          <div className="pages_components">
            <MainButton onClick={handleAddClick}>
              <FaPlus size={20} />
            </MainButton>
            <div className="pages_table">
              <Table<WarehouseDataType>
                scroll={{ x: "100%" }}
                columns={columns}
                dataSource={warehouse}
                rowKey={(record) => record.id.toString()}
                pagination={false}
              />
            </div>
          </div>
        </div>
      )}

      <Modal
        title={isEditing ? "Anbarı ​​Redaktə et" : "Yeni Anbar əlavə edin"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        style={{ minWidth: "340px" }}
        centered
      >
        <form className="modal_form" onSubmit={handleSubmit}>
          <div className="form_input">
            <label className="label" htmlFor="name">
              Anbar adı
            </label>
            <div className="input_wrapper">
              <input
                name="warehouse_name"
                id="warehouse_name"
                placeholder="Anbarın adı"
                value={values.warehouse_name}
                onChange={handleChange}
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage
              error={errors.warehouse_name}
              touched={touched.warehouse_name}
            />
          </div>
          <div className="form_input">
            <label className="label" htmlFor="hex">
              Anbarın kodu
            </label>
            <div className="input_wrapper">
              <input
                name="warehouse_code"
                id="warehouse_code"
                placeholder="Anbarın kodu"
                value={values.warehouse_code}
                onChange={handleChange}
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage
              error={errors.warehouse_code}
              touched={touched.warehouse_code}
            />
          </div>
          <div>
            <MainButton full type="submit" className="form_add_btn">
              {isEditing ? "Redaktə edin" : "Əlavə edin"}
            </MainButton>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Warehouse;
